import { H5 } from '@wheely/ui-kit';
import cn from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import { SelectFieldItem } from '@src-common/view/adapters/SelectField';

import { DataField } from '../DataField';

import s from './styles.module.css';

type RowWrapperProps = {
  children?: React.ReactNode;
};

type RowWrapper = (props: RowWrapperProps) => React.ReactNode;

interface IDescriptionListProps<T extends string = string> {
  values: Record<T, any>;
  titles: Array<[T, React.ReactNode, RowWrapper?]>;
  itemsForSelect?: Partial<Record<T, SelectFieldItem[]>>;
  title?: string;
  className?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
}

const isNotEmpty = (value: any) => value !== undefined && value !== null && value !== '';

const DescriptionList = <T extends string = string>(props: IDescriptionListProps<T>) => {
  const { values, itemsForSelect, title: mainTitle, titles, className, children, action } = props;

  const intl = useIntl();

  const renderValue = (key: T, value: any) => {
    if (value instanceof Date) {
      return intl.formatDate(value);
    }

    if (itemsForSelect && key in itemsForSelect) {
      const items = itemsForSelect[key];
      const foundTitle = items?.find(item => item.value === value)?.title;

      if (foundTitle) {
        return foundTitle;
      }
    }

    if (value === null) {
      return '-';
    }

    return String(value);
  };

  return (
    <div className={cn(s.root, className)} data-test-id="key-value-list">
      {mainTitle && (
        <H5 capitalize={true} className={s.title}>
          <div data-test-id="key-value-list__title">{mainTitle}</div>
          {action && <div data-test-id="key-value-list__action">{action}</div>}
        </H5>
      )}
      <div className={s.list}>
        {titles
          .filter(([key]) => isNotEmpty(values[key]))
          .map(([key, title, Wrapper = React.Fragment]) => (
            <Wrapper key={key}>
              <DataField title={title}>
                <span className={s.valueWrapper}>
                  {typeof values[key] === 'function'
                    ? values[key]()
                    : renderValue(key, values[key])}
                </span>
              </DataField>
            </Wrapper>
          ))}
      </div>
      <div data-test-id="key-value-list__note">{children}</div>
    </div>
  );
};

export { DescriptionList };
export type { IDescriptionListProps, RowWrapper };
